import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { ChevronLeft, Plus, Trash2, Save, X, Utensils, UserPlus, Search, Copy, Sparkles, CheckCircle2 } from "lucide-react";
import AiAssistModal from "@/components/AiAssistModal";

const FOOD_CATEGORIES = ["All", "Protein", "Carbs", "Fats", "Fruit", "Vegetable", "Dairy", "Condiment"];

function FoodPicker({ open, onClose, onPick }) {
    const [foods, setFoods] = useState([]);
    const [q, setQ] = useState("");
    const [cat, setCat] = useState("All");
    const [loading, setLoading] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [newFood, setNewFood] = useState({ name: "", category: "Protein", serving_qty: 1, serving_unit: "serving", kcal: 0, protein_g: 0, carbs_g: 0, fat_g: 0 });
    const [createErr, setCreateErr] = useState("");

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        const params = {};
        if (q) params.q = q;
        if (cat !== "All") params.category = cat;
        api.get("/foods", { params })
            .then(({ data }) => setFoods(data))
            .finally(() => setLoading(false));
    }, [open, q, cat]);

    const createFood = async (e) => {
        e.preventDefault();
        setCreateErr("");
        try {
            const { data } = await api.post("/foods", newFood);
            onPick(data);
            setShowCreate(false);
            setNewFood({ name: "", category: "Protein", serving_qty: 1, serving_unit: "serving", kcal: 0, protein_g: 0, carbs_g: 0, fat_g: 0 });
        } catch (e) {
            setCreateErr(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    if (!open) return null;
    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="food-picker">
            <div className="bg-[var(--surface)] border border-[var(--border-bright)] w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-5 border-b border-[var(--border)]">
                    <div>
                        <p className="label-tiny mb-1">/ Food library</p>
                        <h3 className="font-serif text-2xl">Pick a food</h3>
                    </div>
                    <button onClick={onClose} aria-label="Close"><X size={18} /></button>
                </div>
                <div className="p-4 border-b border-[var(--border)] space-y-3">
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                        <input className="input-dark pl-9" placeholder="Search foods…" value={q} onChange={(e) => setQ(e.target.value)} autoFocus data-testid="food-picker-search" />
                    </div>
                    <div className="flex gap-1 overflow-x-auto">
                        {FOOD_CATEGORIES.map((c) => (
                            <button key={c} onClick={() => setCat(c)} className={`shrink-0 px-3 py-1 text-[11px] font-mono tracking-[0.2em] uppercase border transition-colors ${cat === c ? "bg-[var(--gold)] text-[#0a0908] border-[var(--gold)]" : "border-[var(--border)] hover:border-[var(--gold-deep)]"}`} data-testid={`food-cat-${c.toLowerCase()}`}>
                                {c}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="flex-1 overflow-auto">
                    {loading && <p className="p-5 text-sm text-[var(--text-muted)]">Loading…</p>}
                    {!loading && foods.length === 0 && (
                        <p className="p-5 text-sm text-[var(--text-muted)]">No foods match — try a different search or add a new food below.</p>
                    )}
                    {foods.map((f) => (
                        <button
                            key={f.id}
                            onClick={() => onPick(f)}
                            data-testid={`pick-food-${f.id}`}
                            className="w-full text-left px-5 py-3 border-b border-[var(--border)] hover:bg-[var(--surface-hover)] flex items-center justify-between gap-4"
                        >
                            <div className="min-w-0 flex-1">
                                <p className="font-medium truncate">{f.name}</p>
                                <p className="text-xs text-[var(--text-muted)] font-mono">
                                    {f.serving_qty} {f.serving_unit} · {f.category}
                                </p>
                            </div>
                            <div className="text-right shrink-0 text-xs font-mono">
                                <p className="text-[var(--gold-bright)]">{f.kcal} kcal</p>
                                <p className="text-[var(--text-muted)]">P{f.protein_g} · C{f.carbs_g} · F{f.fat_g}</p>
                            </div>
                        </button>
                    ))}
                </div>
                <div className="p-3 border-t border-[var(--border)]">
                    {!showCreate ? (
                        <button onClick={() => setShowCreate(true)} className="btn-ghost w-full inline-flex items-center justify-center gap-2 text-xs" data-testid="open-create-food">
                            <Plus size={12} /> Add a food that's not on this list
                        </button>
                    ) : (
                        <form onSubmit={createFood} className="space-y-3" data-testid="create-food-form">
                            <div className="grid grid-cols-2 gap-2">
                                <input required placeholder="Food name" className="input-dark text-sm" value={newFood.name} onChange={(e) => setNewFood({ ...newFood, name: e.target.value })} data-testid="new-food-name" />
                                <select className="input-dark text-sm" value={newFood.category} onChange={(e) => setNewFood({ ...newFood, category: e.target.value })}>
                                    {FOOD_CATEGORIES.filter((c) => c !== "All").map((c) => <option key={c}>{c}</option>)}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <input required type="number" step="0.1" placeholder="Serving qty" className="input-dark text-sm" value={newFood.serving_qty} onChange={(e) => setNewFood({ ...newFood, serving_qty: parseFloat(e.target.value) })} />
                                <input required placeholder="Serving unit (e.g. cup, oz)" className="input-dark text-sm" value={newFood.serving_unit} onChange={(e) => setNewFood({ ...newFood, serving_unit: e.target.value })} />
                            </div>
                            <div className="grid grid-cols-4 gap-2">
                                <input required type="number" placeholder="kcal" className="input-dark text-sm" value={newFood.kcal} onChange={(e) => setNewFood({ ...newFood, kcal: parseInt(e.target.value) || 0 })} />
                                <input required type="number" step="0.1" placeholder="P (g)" className="input-dark text-sm" value={newFood.protein_g} onChange={(e) => setNewFood({ ...newFood, protein_g: parseFloat(e.target.value) || 0 })} />
                                <input required type="number" step="0.1" placeholder="C (g)" className="input-dark text-sm" value={newFood.carbs_g} onChange={(e) => setNewFood({ ...newFood, carbs_g: parseFloat(e.target.value) || 0 })} />
                                <input required type="number" step="0.1" placeholder="F (g)" className="input-dark text-sm" value={newFood.fat_g} onChange={(e) => setNewFood({ ...newFood, fat_g: parseFloat(e.target.value) || 0 })} />
                            </div>
                            {createErr && <div className="text-xs text-[var(--danger)]">{createErr}</div>}
                            <div className="flex gap-2">
                                <button type="button" onClick={() => setShowCreate(false)} className="btn-ghost text-xs flex-1">Cancel</button>
                                <button type="submit" className="btn-primary text-xs flex-1" data-testid="save-new-food">Add & select</button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}

function AssignModal({ open, onClose, planId, planName }) {
    const [clients, setClients] = useState([]);
    const [clientId, setClientId] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [done, setDone] = useState(false);
    useEffect(() => { if (open) api.get("/clients").then(({ data }) => { setClients(data); if (data[0]) setClientId(data[0].id); }); }, [open]);
    if (!open) return null;
    const submit = async (e) => {
        e.preventDefault(); setBusy(true); setErr(""); setDone(false);
        try { await api.post(`/meal-plans/${planId}/assign`, { client_id: clientId }); setDone(true); }
        catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="mp-assign-modal">
            <div className="bg-[var(--surface)] border border-[var(--border-bright)] w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-5 border-b border-[var(--border)]">
                    <h3 className="font-serif text-2xl">Assign meal plan</h3>
                    <button onClick={onClose}><X size={18} /></button>
                </div>
                {done ? (
                    <div className="p-5 space-y-4">
                        <p className="text-[var(--gold-bright)] font-medium">Assigned ✓</p>
                        <button onClick={onClose} className="btn-primary w-full">Close</button>
                    </div>
                ) : (
                    <form onSubmit={submit} className="p-5 space-y-4">
                        <p className="text-sm text-[var(--text-muted)]">Assign <span className="text-[var(--text)]">{planName}</span> to:</p>
                        <select required className="input-dark" value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="mp-assign-client">
                            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        {err && <div className="text-sm text-[var(--danger)]">{err}</div>}
                        <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="mp-assign-confirm">
                            {busy ? "Assigning…" : "Assign"}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}

// Helpers to compute macros from structured items [{food, servings}]
function itemMacros(item) {
    const s = Number(item.servings || 1);
    return {
        kcal: Math.round((item.kcal || 0) * s),
        p: +((item.protein_g || 0) * s).toFixed(1),
        c: +((item.carbs_g || 0) * s).toFixed(1),
        f: +((item.fat_g || 0) * s).toFixed(1),
    };
}
function mealTotals(meal) {
    return (meal.items || []).reduce((acc, it) => {
        // Support both new {food_id, kcal, ...} and legacy string items
        if (typeof it === "string") return acc;
        const m = itemMacros(it);
        acc.kcal += m.kcal; acc.p += m.p; acc.c += m.c; acc.f += m.f;
        return acc;
    }, { kcal: 0, p: 0, c: 0, f: 0 });
}

export default function MealPlanBuilderPage() {
    const { id } = useParams();
    const nav = useNavigate();
    const [qs, setQs] = useSearchParams();
    const [plan, setPlan] = useState(null);
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const [assignOpen, setAssignOpen] = useState(false);
    const [aiOpen, setAiOpen] = useState(false);
    const [aiFlash, setAiFlash] = useState(null);
    const [picker, setPicker] = useState({ open: false, mealIdx: null });

    useEffect(() => {
        api.get(`/meal-plans/${id}`).then(({ data }) => {
            setPlan(data);
            // auto-open AI modal if ?ai=generate or ?ai=1
            if (qs.get("ai")) {
                setAiOpen(true);
                qs.delete("ai");
                setQs(qs, { replace: true });
            }
        });
    }, [id]); // eslint-disable-line

    const totals = useMemo(() => {
        if (!plan) return { kcal: 0, p: 0, c: 0, f: 0 };
        return (plan.meals || []).reduce((acc, m) => {
            const t = mealTotals(m);
            acc.kcal += t.kcal; acc.p += t.p; acc.c += t.c; acc.f += t.f;
            return acc;
        }, { kcal: 0, p: 0, c: 0, f: 0 });
    }, [plan]);

    if (!plan) return <div className="p-8 text-[var(--text-muted)]">Loading…</div>;

    const set = (patch) => setPlan({ ...plan, ...patch });
    const addMeal = () => {
        const n = (plan.meals || []).length + 1;
        set({ meals: [...(plan.meals || []), { name: `Meal ${n}`, items: [], notes: "" }] });
    };
    const removeMeal = (i) => set({ meals: plan.meals.filter((_, ix) => ix !== i) });
    const updateMeal = (i, patch) => { const meals = [...plan.meals]; meals[i] = { ...meals[i], ...patch }; set({ meals }); };
    const addFoodToMeal = (i, food) => {
        const meals = [...plan.meals];
        const items = [...(meals[i].items || [])];
        items.push({
            food_id: food.id,
            name: food.name,
            servings: 1,
            serving_qty: food.serving_qty,
            serving_unit: food.serving_unit,
            kcal: food.kcal,
            protein_g: food.protein_g,
            carbs_g: food.carbs_g,
            fat_g: food.fat_g,
        });
        meals[i].items = items;
        set({ meals });
    };
    const updateItem = (i, j, patch) => {
        const meals = [...plan.meals];
        meals[i].items[j] = { ...meals[i].items[j], ...patch };
        set({ meals });
    };
    const removeItem = (i, j) => {
        const meals = [...plan.meals];
        meals[i].items.splice(j, 1);
        set({ meals });
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/meal-plans/${id}`, {
                name: plan.name, description: plan.description || "",
                daily_calories: plan.daily_calories, protein_g: plan.protein_g, carbs_g: plan.carbs_g, fat_g: plan.fat_g,
                meals: plan.meals,
            });
            setSavedAt(new Date());
        } finally { setSaving(false); }
    };

    const applyAi = (result) => {
        // Items from the AI now come back as structured objects (matched to library when possible).
        const aiMeals = (result.meals || []).map((m) => ({
            name: m.name || "Meal",
            items: (m.items || []).map((it) => {
                if (typeof it === "string") return it; // legacy fallback
                if (it.food_id) {
                    return {
                        food_id: it.food_id,
                        name: it.name,
                        servings: Number(it.servings) || 1,
                        serving_qty: it.serving_qty,
                        serving_unit: it.serving_unit,
                        kcal: it.kcal,
                        protein_g: it.protein_g,
                        carbs_g: it.carbs_g,
                        fat_g: it.fat_g,
                    };
                }
                return {
                    food_id: null,
                    name: it.name || "Unmatched food",
                    servings: 1,
                    serving_qty: it.serving_qty ?? 1,
                    serving_unit: it.serving_unit || "serving",
                    kcal: it.kcal || 0,
                    protein_g: it.protein_g || 0,
                    carbs_g: it.carbs_g || 0,
                    fat_g: it.fat_g || 0,
                };
            }),
            notes: m.notes || "",
        }));
        // If the builder still has only the empty template meals, replace them; otherwise append.
        const existingMeals = (plan.meals || []).filter((m) => (m.items || []).length > 0);
        const newMeals = existingMeals.length ? [...existingMeals, ...aiMeals] : aiMeals;
        // Always overwrite daily macro targets with AI totals so the coach sees them populate.
        set({
            name: result.name || plan.name,
            description: result.description || plan.description || "",
            daily_calories: Number(result.daily_calories) || plan.daily_calories,
            protein_g: Number(result.protein_g) || plan.protein_g,
            carbs_g: Number(result.carbs_g) || plan.carbs_g,
            fat_g: Number(result.fat_g) || plan.fat_g,
            meals: newMeals,
        });
        setAiFlash({
            kcal: result.daily_calories,
            p: result.protein_g,
            c: result.carbs_g,
            f: result.fat_g,
            matched: result.match_stats?.matched || 0,
            total: result.match_stats?.total || 0,
        });
        setTimeout(() => setAiFlash(null), 6000);
    };

    return (
        <div className="p-8 space-y-6" data-testid="meal-plan-builder-page">
            {aiFlash && (
                <div className="border border-[var(--gold)] bg-[var(--surface)] p-4 flex items-start gap-3 animate-in fade-in" data-testid="ai-flash-banner">
                    <CheckCircle2 size={18} className="text-[var(--gold-bright)] shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="font-serif text-xl mb-1">AI meal plan applied</p>
                        <p className="text-xs font-mono text-[var(--text-muted)]">
                            Daily targets set to <span className="text-[var(--gold-bright)]">{aiFlash.kcal} kcal · P{aiFlash.p} · C{aiFlash.c} · F{aiFlash.f}</span>
                            {aiFlash.total > 0 && <span> · Food Library matched {aiFlash.matched}/{aiFlash.total}</span>}
                        </p>
                    </div>
                    <button onClick={() => setAiFlash(null)} className="text-[var(--text-muted)] hover:text-[var(--text)]"><X size={14} /></button>
                </div>
            )}
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div className="flex-1 min-w-[260px]">
                    <Link to="/meal-plans" className="inline-flex items-center gap-1 text-sm text-[var(--text-muted)] hover:text-[var(--gold)] mb-3"><ChevronLeft size={14} /> Meal plans</Link>
                    <input className="bg-transparent border-b border-transparent hover:border-[var(--border)] focus:border-[var(--gold)] outline-none font-serif text-5xl w-full max-w-2xl" value={plan.name} onChange={(e) => set({ name: e.target.value })} data-testid="mp-name-input" />
                    <input className="bg-transparent border-b border-transparent hover:border-[var(--border)] focus:border-[var(--gold)] outline-none text-sm text-[var(--text-muted)] mt-2 w-full max-w-2xl" placeholder="Description…" value={plan.description || ""} onChange={(e) => set({ description: e.target.value })} />
                </div>
                <div className="flex items-center gap-3">
                    {savedAt && <span className="font-mono text-xs text-[var(--text-muted)]">saved {savedAt.toLocaleTimeString()}</span>}
                    <button onClick={() => setAiOpen(true)} className="btn-ghost inline-flex items-center gap-2 border-[var(--gold-deep)]" data-testid="mp-ai-btn">
                        <Sparkles size={14} className="text-[var(--gold-bright)]" /> AI Assist
                    </button>
                    <button onClick={async () => { const { data } = await api.post(`/meal-plans/${id}/duplicate`); nav(`/meal-plans/${data.id}`); }} className="btn-ghost inline-flex items-center gap-2" title="Duplicate plan" data-testid="mp-duplicate-btn"><Copy size={14} /> Duplicate</button>
                    <button onClick={save} className="btn-ghost inline-flex items-center gap-2" disabled={saving} data-testid="mp-save-btn"><Save size={14} /> {saving ? "…" : "Save"}</button>
                    <button onClick={() => setAssignOpen(true)} className="btn-primary inline-flex items-center gap-2" data-testid="mp-open-assign"><UserPlus size={14} /> Assign</button>
                </div>
            </div>

            {/* Targets + Totals side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="mp-macros-grid">
                <div className="border border-[var(--border)] bg-[var(--surface)] p-5">
                    <p className="label-tiny mb-3">/ Daily targets (manual)</p>
                    <div className="grid grid-cols-4 gap-3">
                        {[
                            { k: "daily_calories", label: "KCAL", test: "mp-kcal" },
                            { k: "protein_g", label: "PROTEIN", test: "mp-protein" },
                            { k: "carbs_g", label: "CARBS", test: "mp-carbs" },
                            { k: "fat_g", label: "FAT", test: "mp-fat" },
                        ].map(({ k, label, test }) => (
                            <div key={k}>
                                <p className="label-tiny mb-1">{label}</p>
                                <input type="number" className="input-dark" value={plan[k] ?? 0} onChange={(e) => set({ [k]: parseInt(e.target.value) || 0 })} data-testid={test} />
                            </div>
                        ))}
                    </div>
                </div>
                <div className="border border-[var(--gold-deep)] bg-[var(--surface)] p-5" data-testid="mp-totals-card">
                    <p className="label-tiny mb-3 text-[var(--gold-bright)]">/ Live totals (from foods)</p>
                    <div className="grid grid-cols-4 gap-3">
                        <div><p className="label-tiny mb-1">KCAL</p><p className="font-display text-3xl">{totals.kcal}</p></div>
                        <div><p className="label-tiny mb-1">P</p><p className="font-display text-3xl">{Math.round(totals.p)}g</p></div>
                        <div><p className="label-tiny mb-1">C</p><p className="font-display text-3xl">{Math.round(totals.c)}g</p></div>
                        <div><p className="label-tiny mb-1">F</p><p className="font-display text-3xl">{Math.round(totals.f)}g</p></div>
                    </div>
                </div>
            </div>

            <div className="space-y-4">
                {(plan.meals || []).map((m, i) => {
                    const t = mealTotals(m);
                    return (
                        <div key={i} className="border border-[var(--border)] bg-[var(--surface)]" data-testid={`mp-meal-${i}`}>
                            <div className="flex items-center justify-between p-4 border-b border-[var(--border)] gap-3">
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <Utensils size={16} className="text-[var(--gold)] shrink-0" />
                                    <input className="bg-transparent border-b border-transparent hover:border-[var(--border)] focus:border-[var(--gold)] outline-none font-display text-2xl tracking-[0.05em] flex-1 min-w-0" value={m.name} onChange={(e) => updateMeal(i, { name: e.target.value })} />
                                </div>
                                <div className="text-right shrink-0 font-mono text-xs">
                                    <p className="text-[var(--gold-bright)]">{t.kcal} kcal</p>
                                    <p className="text-[var(--text-muted)]">P{Math.round(t.p)} · C{Math.round(t.c)} · F{Math.round(t.f)}</p>
                                </div>
                                <button onClick={() => removeMeal(i)} className="text-[var(--text-muted)] hover:text-[var(--danger)] shrink-0"><Trash2 size={14} /></button>
                            </div>
                            <div className="p-4 space-y-2">
                                {(m.items || []).map((item, j) => {
                                    if (typeof item === "string") {
                                        return (
                                            <div key={j} className="flex gap-2 items-center text-sm text-[var(--text-muted)]">
                                                <span className="flex-1 italic">{item} <span className="text-[var(--text-faint)]">(legacy — remove & re-add from library)</span></span>
                                                <button onClick={() => removeItem(i, j)} className="text-[var(--text-muted)] hover:text-[var(--danger)]"><Trash2 size={13} /></button>
                                            </div>
                                        );
                                    }
                                    const im = itemMacros(item);
                                    return (
                                        <div key={j} className="grid grid-cols-12 gap-2 items-center" data-testid={`mp-item-${i}-${j}`}>
                                            <div className="col-span-5 min-w-0">
                                                <p className="text-sm truncate">{item.name}</p>
                                                <p className="text-[10px] text-[var(--text-muted)] font-mono">{item.serving_qty} {item.serving_unit}</p>
                                            </div>
                                            <div className="col-span-2 flex items-center gap-1">
                                                <input
                                                    type="number" min="0.25" step="0.25"
                                                    className="input-dark py-1.5 text-sm text-center"
                                                    value={item.servings}
                                                    onChange={(e) => updateItem(i, j, { servings: parseFloat(e.target.value) || 0 })}
                                                    data-testid={`mp-servings-${i}-${j}`}
                                                />
                                                <span className="text-[10px] text-[var(--text-muted)]">×</span>
                                            </div>
                                            <div className="col-span-4 text-right font-mono text-xs">
                                                <p className="text-[var(--gold-bright)]">{im.kcal} kcal</p>
                                                <p className="text-[var(--text-muted)]">P{im.p} · C{im.c} · F{im.f}</p>
                                            </div>
                                            <button onClick={() => removeItem(i, j)} className="col-span-1 text-[var(--text-muted)] hover:text-[var(--danger)] flex justify-center"><Trash2 size={13} /></button>
                                        </div>
                                    );
                                })}
                                <button onClick={() => setPicker({ open: true, mealIdx: i })} className="btn-ghost mt-2 inline-flex items-center gap-1 text-xs" data-testid={`mp-add-food-${i}`}>
                                    <Plus size={12} /> Add food from library
                                </button>
                                <input className="input-dark mt-2" placeholder="Notes (e.g. pre-workout, post-workout)" value={m.notes || ""} onChange={(e) => updateMeal(i, { notes: e.target.value })} />
                            </div>
                        </div>
                    );
                })}
                <button onClick={addMeal} className="w-full border border-dashed border-[var(--border-bright)] p-6 text-[var(--text-muted)] hover:text-[var(--gold)] hover:border-[var(--gold)] inline-flex items-center justify-center gap-2" data-testid="mp-add-meal-btn">
                    <Plus size={14} /> Add meal
                </button>
            </div>

            <FoodPicker
                open={picker.open}
                onClose={() => setPicker({ open: false, mealIdx: null })}
                onPick={(food) => { addFoodToMeal(picker.mealIdx, food); setPicker({ open: false, mealIdx: null }); }}
            />
            <AssignModal open={assignOpen} onClose={() => setAssignOpen(false)} planId={id} planName={plan.name} />
            <AiAssistModal open={aiOpen} onClose={() => setAiOpen(false)} mode="mealplan" onApply={applyAi} />
        </div>
    );
}
